import React, { useState } from "react";
import { toast } from "sonner";
import { Plus, Minus } from "lucide-react";
import api from "@/lib/api";
import { Button, Input } from "@/components/ui-kit";
import { parseQty } from "@/lib/format";

/**
 * Two-row quick stock adjuster — used inside the Stock edit modal.
 * Row 1: Add (positive) — Plus icon, green.
 * Row 2: Remove/Loss (negative) — Minus icon, red.
 * Each row has independent qty input + Terapkan button, no formula (unlike PriceAdjustment).
 */
export function StockQuickAdjust({ stockId, unit, onDone }) {
  const [addQty, setAddQty] = useState("");
  const [removeQty, setRemoveQty] = useState("");
  const [addNote, setAddNote] = useState("");
  const [removeNote, setRemoveNote] = useState("");
  const [busy, setBusy] = useState(false);

  const apply = async (direction) => {
    const qtyStr = direction === "add" ? addQty : removeQty;
    const note = direction === "add" ? addNote : removeNote;
    const qty = parseQty(qtyStr);
    if (!qty || qty <= 0) { toast.error("Isi jumlah > 0"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/stocks/${stockId}/adjust`, { quantity: qty, direction, note });
      toast.success(`Stok ${direction === "add" ? "ditambah" : "dikurangi"} ${qty} ${unit} · saldo: ${r.data.new_quantity}`);
      if (direction === "add") { setAddQty(""); setAddNote(""); }
      else { setRemoveQty(""); setRemoveNote(""); }
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-12 gap-2 items-end" data-testid="stock-quick-add">
        <div className="col-span-3">
          <label className="block text-[10px] uppercase tracking-wider font-semibold text-emerald-700 mb-1">Tambah Stok</label>
          <Input
            className="font-mono-num"
            value={addQty}
            onChange={(e) => setAddQty(e.target.value)}
            placeholder="0"
            data-testid="stock-add-qty"
          />
        </div>
        <div className="col-span-7">
          <label className="block text-[10px] uppercase tracking-wider font-semibold text-stone-500 mb-1">Catatan</label>
          <Input
            value={addNote}
            onChange={(e) => setAddNote(e.target.value)}
            placeholder="contoh: Restock harian, pembelian PO 123"
            data-testid="stock-add-note"
          />
        </div>
        <div className="col-span-2">
          <Button
            onClick={() => apply("add")}
            disabled={busy}
            className="!bg-emerald-600 hover:!bg-emerald-700 w-full text-xs"
            data-testid="stock-add-btn"
          >
            <Plus className="h-3.5 w-3.5" /> Tambah
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-12 gap-2 items-end" data-testid="stock-quick-remove">
        <div className="col-span-3">
          <label className="block text-[10px] uppercase tracking-wider font-semibold text-red-700 mb-1">Kurangi Stok</label>
          <Input
            className="font-mono-num"
            value={removeQty}
            onChange={(e) => setRemoveQty(e.target.value)}
            placeholder="0"
            data-testid="stock-remove-qty"
          />
        </div>
        <div className="col-span-7">
          <label className="block text-[10px] uppercase tracking-wider font-semibold text-stone-500 mb-1">Catatan / Alasan</label>
          <Input
            value={removeNote}
            onChange={(e) => setRemoveNote(e.target.value)}
            placeholder="contoh: koreksi opname, transfer ke proyek lain"
            data-testid="stock-remove-note"
          />
        </div>
        <div className="col-span-2">
          <Button
            onClick={() => apply("remove")}
            disabled={busy}
            className="!bg-red-600 hover:!bg-red-700 w-full text-xs"
            data-testid="stock-remove-btn"
          >
            <Minus className="h-3.5 w-3.5" /> Kurangi
          </Button>
        </div>
      </div>
      <p className="text-[10px] text-stone-400 italic">
        Untuk laporan kerusakan atau kehilangan dengan foto + kronologi, gunakan tombol <b>Lapor Rusak/Hilang</b> di tabel stok.
      </p>
    </div>
  );
}
